import React, { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { FACILITY_ROOMS } from '../../game/phaserFacility/facilityLayout';
import { FacilityScene3D } from './three/FacilityScene3D';
import { FacilityPostFX } from './three/FacilityPostFX';
import { facilityCenterTarget } from './three/facilityCoords';
import { RoomDrawer } from './RoomDrawer';
import { CalendarBadge } from './CalendarBadge';

// Orbit angle constraints (radians)
const MIN_POLAR = Math.PI / 9;         // 20° — can't look from below
const MAX_POLAR = (7 * Math.PI) / 18;  // 70° — can't look straight down
const MIN_AZIMUTH = 0;                 // east view
const MAX_AZIMUTH = Math.PI / 2;       // north view (player sees facility from southeast to northeast)

// Initial camera position: 45° azimuth, 45° polar, distance ~22
// Computed for facility center ≈ (7, 0, 5.5)
const CAMERA_INITIAL = [19, 16, 18];

/**
 * R3F Canvas wrapper for the 3D facility map.
 * Replaces IsometricFacilityMap — same props, same interaction contract.
 */
export function ThreeFacilityMap({ state, dispatch }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRoomId, setDrawerRoomId] = useState(null);

  const selectedFacilityRoomId = state.ui?.selectedFacilityRoomId || null;
  const orbitTarget = facilityCenterTarget();

  function resolveStateRoom(roomDef) {
    if (roomDef.type === 'marketing') {
      const unlocked = state.unlockedTech?.includes('D4') || false;
      return { id: 'room-marketing', unlocked, type: 'marketing', name: 'Marketing Office' };
    }
    if (!roomDef.stateId) return null;
    return state.rooms.find(r => r.id === roomDef.stateId) || null;
  }

  function handleRoomClick(roomDef) {
    if (roomDef.ghost) return;
    const newId = roomDef.id;
    if (selectedFacilityRoomId === newId && drawerOpen) {
      setDrawerOpen(false);
      dispatch({ type: 'CLOSE_FACILITY_DRAWER' });
      return;
    }
    dispatch({ type: 'SET_SELECTED_FACILITY_ROOM', payload: { roomId: newId } });
    setDrawerOpen(true);
    setDrawerRoomId(newId);
  }

  function handleDrawerClose() {
    setDrawerOpen(false);
    dispatch({ type: 'CLOSE_FACILITY_DRAWER' });
  }

  const drawerRoomDef = FACILITY_ROOMS.find(r => r.id === drawerRoomId) || null;
  const drawerStateRoom = drawerRoomDef ? resolveStateRoom(drawerRoomDef) : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', overflow: 'hidden' }}>
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <CalendarBadge state={state} />
        <Canvas
          shadows={{ type: 'PCFSoftShadowMap' }}
          camera={{ fov: 50, position: CAMERA_INITIAL, near: 0.1, far: 200 }}
          gl={{ antialias: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <FacilityScene3D
            state={state}
            selectedFacilityRoomId={selectedFacilityRoomId}
            onRoomClick={handleRoomClick}
          />
          <FacilityPostFX />
          <OrbitControls
            target={orbitTarget}
            minPolarAngle={MIN_POLAR}
            maxPolarAngle={MAX_POLAR}
            minAzimuthAngle={MIN_AZIMUTH}
            maxAzimuthAngle={MAX_AZIMUTH}
            minDistance={5}
            maxDistance={40}
            enableDamping
            dampingFactor={0.08}
          />
        </Canvas>
      </div>

      <RoomDrawer
        open={drawerOpen}
        roomDef={drawerRoomDef}
        room={drawerStateRoom}
        state={state}
        dispatch={dispatch}
        onClose={handleDrawerClose}
      />
    </div>
  );
}
